// Phase 16 · Notification preferences (per-user toggle grid)
import { useEffect, useState } from "react";
import { Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const TYPE_LABELS = {
  approval_pending:       "New pending approval",
  approval_approved:      "Approval granted",
  approval_rejected:      "Approval rejected",
  missed_visit_alert:     "Missed visit alert",
  order_status_changed:   "Order status changed",
  primary_order_pending:  "Primary order awaiting approval",
  target_at_risk:         "Target at risk",
  claim_status_changed:   "Claim status changed",
  payment_status_changed: "Payment status changed",
};

export default function NotificationPrefsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/notification-preferences");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load preferences");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (row, field) => {
    setSaving(row.type + "-" + field);
    try {
      const payload = {
        type: row.type,
        in_app_enabled: field === "in_app" ? !row.in_app_enabled : row.in_app_enabled,
        email_enabled: field === "email" ? !row.email_enabled : row.email_enabled,
      };
      await api.put(`/notification-preferences/${row.type}`, payload);
      setItems(ls => ls.map(x => x.type === row.type ? { ...x, ...payload } : x));
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(null); }
  };

  if (loading) return <div className="flex justify-center py-20 text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="notif-prefs-page">
      <PageHeader
        breadcrumbs={[{ label: "Notifications", to: "/" }, { label: "Preferences" }]}
        title="Notification preferences"
        subtitle="Which events send you an in-app or email notification."
        testid="notificationprefspage-page-header"
      />

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#E5197F]" /> Notification Preferences
        </h1>
        <p className="text-xs text-neutral-500 mt-1">Choose which notifications you receive in-app and via email.</p>
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-xs">
            <tr>
              <th className="text-left px-4 py-2">Notification</th>
              <th className="w-24 text-center">In-app</th>
              <th className="w-24 text-center">Email</th>
            </tr>
          </thead>
          <tbody>
            {items.map(row => (
              <tr key={row.type} className="border-t border-neutral-100" data-testid={`pref-row-${row.type}`}>
                <td className="px-4 py-2.5">{TYPE_LABELS[row.type] || row.type}</td>
                <td className="text-center">
                  <input
                    type="checkbox" checked={!!row.in_app_enabled}
                    onChange={() => toggle(row, "in_app")}
                    disabled={saving === row.type + "-in_app"}
                    data-testid={`pref-inapp-${row.type}`}
                    className="h-4 w-4 accent-[#E5197F]"
                  />
                </td>
                <td className="text-center">
                  <input
                    type="checkbox" checked={!!row.email_enabled}
                    onChange={() => toggle(row, "email")}
                    disabled={saving === row.type + "-email"}
                    data-testid={`pref-email-${row.type}`}
                    className="h-4 w-4 accent-[#E5197F]"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
